import { Autocomplete, Checkbox, FormControlLabel, TextField } from '@mui/material'
import { debounce } from '../../helpers/debounce'
import { countries, states } from '../../helpers/countries'
import { LandlineSubscriberFormData } from '../../models/add-phone-number.model'
import { OrganizationDetails, OrganizationModel } from '../../models/organization.model'
import { ExternalServices } from '../../services/external.service'
import { buttonLabels } from '../shared/constants'
import {
    ImportColumnChip,
    ImportInfoBanner,
    ImportStepCard,
    ImportStepFooter,
    ImportStepLayout,
    NUMBER_IMPORT_COLUMNS
} from './import-numbers-shared.component'

interface IProps {
    Organizations: OrganizationModel[]
    SelectedOrgId: string
    OrgDetails: OrganizationDetails | null
    Address: LandlineSubscriberFormData
    UseOrgAddress: boolean
    IsDarkMode: boolean
    OnChangeOrg: (organizationId: string) => void
    OnChangeAddress: (address: LandlineSubscriberFormData) => void
    OnToggleUseOrgAddress: (checked: boolean) => void
    OnCancel: () => void
    OnContinue: () => void
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '0.75rem'
    }
}

const AddressTextField = ({
    label,
    name,
    value,
    placeholder,
    onChange
}: {
    label: string
    name: keyof LandlineSubscriberFormData
    value: string
    placeholder: string
    onChange: (name: keyof LandlineSubscriberFormData, value: string) => void
}) => {
    return (
        <div>
            <div className='h5-regular color-text-field-label mb-1'>{label} *</div>
            <TextField
                fullWidth
                size="small"
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={(event) => onChange(name, event.target.value)}
                variant="outlined"
                InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                sx={fieldSx}
                autoComplete='no'
            />
        </div>
    )
}

export const formatAddressSummary = (address: LandlineSubscriberFormData) => {
    const person = [address.FirstName, address.LastName].filter(Boolean).join(" ")
    const street = [address.HouseNumber, address.Address].filter(Boolean).join(" ")
    const countryName = countries.find((country) => country.ISO === address.Country)?.Name || address.Country
    const cityStateZip = [address.City, [address.State, address.ZipCode].filter(Boolean).join(" ")].filter(Boolean).join(", ")
    const location = [cityStateZip, countryName].filter(Boolean).join(", ")
    return [person, street, location].filter(Boolean).join(" · ")
}

const hasRequiredAddress = (address: LandlineSubscriberFormData, requireName: boolean) => {
    const locationComplete = !!(address.HouseNumber?.trim() && address.Address?.trim() && address.ZipCode?.trim() && address.City?.trim() && address.State?.trim() && address.Country?.trim())
    if (!requireName) return locationComplete
    return locationComplete && !!address.FirstName?.trim() && !!address.LastName?.trim()
}

const SelectOrganizationStep = (props: IProps) => {
    const selectedOrganization = props.Organizations.find((org) => org.ID === props.SelectedOrgId) || null
    const organizationOptions = props.Organizations.filter((org) => org.Name !== null && !org.IsDeleted)
    const canContinue = !!props.SelectedOrgId && hasRequiredAddress(props.Address, !props.UseOrgAddress)
    const stateOptions = (states as any)[props.Address.Country] || []

    const handleAddressChange = (name: keyof LandlineSubscriberFormData, value: string) => {
        if (name === "ZipCode") {
            props.OnChangeAddress({
                ...props.Address,
                ZipCode: value,
                City: value === "" ? "" : props.Address.City,
                State: value === "" ? "" : props.Address.State,
                Country: value === "" ? "" : props.Address.Country
            })
            return
        }
        props.OnChangeAddress({ ...props.Address, [name]: value })
    }

    const onZipCodeChange = debounce(() => {
        const currentZipCode = props.Address.ZipCode
        if (currentZipCode.length >= 3) {
            ExternalServices.zipLookup(currentZipCode)
                .then((data) => {
                    props.OnChangeAddress({ ...props.Address, ...data })
                })
                .catch(() => { })
        }
    }, 500)

    return (
        <ImportStepLayout
            isDarkMode={props.IsDarkMode}
            left={
                <>
                    <ImportInfoBanner title="One organization per upload">
                        The organization and address you set here apply to every row in the file. To host numbers for a second organization, run a second upload.
                    </ImportInfoBanner>

                    <div className='h5-medium color-primary-accent mt-3 mb-1'>Organization *</div>
                    <Autocomplete
                        size="small"
                        options={organizationOptions}
                        getOptionLabel={(option) => option?.Name || ""}
                        classes={{ input: 'h2 color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                        onChange={(_event, value) => {
                            props.OnChangeOrg(value?.ID || "")
                        }}
                        value={selectedOrganization}
                        isOptionEqualToValue={(option, value) => option.ID === value.ID}
                        renderOption={(defaultprops, option) => (
                            <li {...defaultprops} className={"c-pointer h2 color-primary-dark p-1 d-flex justify-between w-p-90"}>
                                {option?.Name}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select organization"
                                name="OrganizationId"
                                variant="outlined"
                            />
                        )}
                        sx={fieldSx}
                    />

                    <div className='w-p-100 my-3' style={{ height: 1, backgroundColor: "#E6E6E6" }} />

                    <div className='d-flex justify-between align-center mb-2'>
                        <div className='h3-medium color-primary-accent'>Address for these numbers</div>
                        <FormControlLabel
                            className='m-0'
                            classes={{ label: 'h5-regular w-space' }}
                            control={
                                <Checkbox
                                    checked={props.UseOrgAddress}
                                    disabled={!props.SelectedOrgId}
                                    onChange={(event) => props.OnToggleUseOrgAddress(event.target.checked)}
                                    classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                                />
                            }
                            label="Same as Organization"
                            sx={{ marginRight: 0 }}
                        />
                    </div>

                    {props.UseOrgAddress ? (
                        <div className='d-flex align-start p-2 br-2 bg-color-new-users-step-card-bg b-around-new-users-step-card-border'>
                            <div>
                                <div className='h4-medium color-primary-light'>
                                    {props.OrgDetails?.Name || selectedOrganization?.Name || "Organization"} — registered address
                                </div>
                                <div className='h5-regular color-text-field-label'>
                                    {formatAddressSummary(props.Address) || "Registered address will appear after the organization is selected."}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <AddressTextField
                                label="First Name"
                                name="FirstName"
                                placeholder="First Name"
                                value={props.Address.FirstName}
                                onChange={handleAddressChange}
                            />
                            <AddressTextField
                                label="Last Name"
                                name="LastName"
                                placeholder="Last Name"
                                value={props.Address.LastName}
                                onChange={handleAddressChange}
                            />
                            <AddressTextField
                                label="House Number"
                                name="HouseNumber"
                                placeholder="House Number"
                                value={props.Address.HouseNumber}
                                onChange={handleAddressChange}
                            />
                            <AddressTextField
                                label="Street Name"
                                name="Address"
                                placeholder="Street Name"
                                value={props.Address.Address}
                                onChange={handleAddressChange}
                            />

                            <div>
                                <div className='h5-regular color-text-field-label mb-1'>Zipcode *</div>
                                <TextField
                                    fullWidth
                                    size="small"
                                    name="ZipCode"
                                    placeholder="Zip Code"
                                    value={props.Address.ZipCode}
                                    onChange={(event) => handleAddressChange("ZipCode", event.target.value)}
                                    onKeyUp={onZipCodeChange}
                                    variant="outlined"
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    sx={fieldSx}
                                    autoComplete='no'
                                />
                            </div>
                            <AddressTextField
                                label="City"
                                name="City"
                                placeholder="City"
                                value={props.Address.City}
                                onChange={handleAddressChange}
                            />
                            <div>
                                <div className='h5-regular color-text-field-label mb-1'>State *</div>
                                <Autocomplete
                                    size="small"
                                    options={stateOptions}
                                    value={stateOptions.find((item: any) => item.ISO === props.Address.State) || { ISO: "", Name: "" }}
                                    getOptionLabel={(option: any) => option?.Name || ""}
                                    disabled={!props.Address.Country}
                                    onChange={(_event, value: any) => handleAddressChange("State", value?.ISO || "")}
                                    classes={{ input: 'h2 color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                    renderOption={(defaultprops, option: any) => (
                                        <li {...defaultprops} className={"c-pointer h2 color-primary-dark p-1 d-flex justify-between w-p-90"}>
                                            {option?.Name}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="State" name="State" variant="outlined" />
                                    )}
                                    sx={fieldSx}
                                />
                            </div>
                            <AddressTextField
                                label="State Code"
                                name="StateCode"
                                placeholder="State Code"
                                value={props.Address.StateCode}
                                onChange={handleAddressChange}
                            />
                            <div>
                                <div className='h5-regular color-text-field-label mb-1'>Country *</div>
                                <Autocomplete
                                    size="small"
                                    options={countries}
                                    value={countries.find((item) => item.ISO === props.Address.Country) || { ISO: "", Name: "" }}
                                    getOptionLabel={(option: any) => option?.Name || ""}
                                    onChange={(_event, value: any) => handleAddressChange("Country", value?.ISO || "")}
                                    classes={{ input: 'h2 color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                    renderOption={(defaultprops, option: any) => (
                                        <li {...defaultprops} className={"c-pointer h2 color-primary-dark p-1 d-flex justify-between w-p-90"}>
                                            {option?.Name}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Country" name="country" variant="outlined" />
                                    )}
                                    sx={fieldSx}
                                />
                            </div>
                        </div>
                    )}

                    <ImportStepFooter
                        backLabel={buttonLabels.CANCEL}
                        onBack={props.OnCancel}
                        continueLabel="Continue to upload →"
                        onContinue={props.OnContinue}
                        canContinue={canContinue}
                    />
                </>
            }
            right={
                <>
                    <ImportStepCard isDarkMode={props.IsDarkMode} className="mb-2">
                        <div className='h3-medium color-primary-accent'>Why we ask first</div>
                        <div className='h5-regular color-text-field-label mt-1'>
                            Organization and address are the same for the whole batch, so they live here instead of in spreadsheet columns. Your file only carries what actually changes per row.
                        </div>
                    </ImportStepCard>
                    <ImportStepCard isDarkMode={props.IsDarkMode}>
                        <div className='h3-medium color-primary-accent'>Your file needs two columns</div>
                        <div className='d-flex flex-wrap mt-2'>
                            {NUMBER_IMPORT_COLUMNS.map((column) => (
                                <ImportColumnChip key={column.label} label={column.label} required={column.required} />
                            ))}
                        </div>
                        <div className='h5-regular color-text-field-label mt-1'>
                            That’s the whole template. Next step has the download.
                        </div>
                    </ImportStepCard>
                </>
            }
        />
    )
}

export default SelectOrganizationStep
